import React, { useEffect, useRef, useState } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldLabel,
  Input,
  Kbd,
  Stack,
  useToast,
} from '@eluve/components';
import { getTiptapMarkdownContentFromEditor } from '@eluve/markdown-utils';

import { EluveEditor, EluveEditorHandle } from '../../../EluveEditor';

export interface Snippet {
  id?: string | unknown;
  title: string;
  content: string;
}

export interface SnippetModalProps {
  snippet?: Snippet;
  isOpen: boolean;
  onOpenChange?(open: boolean): void;
  onClose?: () => void;
  onSave?: (title: string, content: string) => void;
}

export const SnippetModal: React.FC<SnippetModalProps> = ({
  snippet,
  isOpen,
  onOpenChange,
  onClose,
  onSave,
}) => {
  const toast = useToast();
  const [title, setTitle] = useState(snippet?.title ?? '');
  const [content, setContent] = useState(snippet?.content ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef<EluveEditorHandle | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTitle(snippet?.title ?? '');
      setContent(snippet?.content ?? '');
    }
  }, [isOpen]);

  const handleSave = () => {
    try {
      setIsSaving(true);
      let savedContent = content;
      if (editorRef.current) {
        // Save the snippet content in markdown
        savedContent = editorRef.current.getMarkdown();
      }
      onSave?.(title, savedContent);
    } catch (e) {
      toast.error('Failed to save snippet');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onOpenChange?.(false);
    onClose?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent size="2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>
            {snippet?.title ? 'Edit Snippet' : 'Create Snippet'}
          </DialogTitle>
          <DialogDescription>
            Define a reusable text block you can insert into notes by typing{' '}
            <Kbd>/</Kbd> followed by the snippet title.
          </DialogDescription>
        </DialogHeader>
        <Stack gap={5} align="stretch">
          <Field>
            <FieldLabel size="sm">Title</FieldLabel>
            <Input
              placeholder="Snippet title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel size="sm">Content</FieldLabel>
            <EluveEditor
              className="h-50 w-full overflow-y-auto rounded-lg border border-border-primary"
              ref={editorRef}
              content={snippet?.content}
              onUpdate={(editor) => {
                setContent(getTiptapMarkdownContentFromEditor(editor));
              }}
            />
          </Field>
        </Stack>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!title || !content || isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
